import {
  Button,
  Col,
  Drawer,
  Form,
  Input,
  Modal,
  Row,
  Space
} from "antd";
import moment from 'moment';
import { useState } from "react";
import { useTaxLegalActions, useTaxLegalCase } from "../../../contexts/taxLegalCaseStore";
import { StageFormNames, useTaxLegalStages } from "../../../contexts/TaxLegalStagesStore";
import UserStore from "../../../contexts/userStore";
import { ActiveStageValues, OriginalEvaluation, TaxLegalCaseRevision, TaxLegalStage, UpdateTaxLegalCase } from "../../../types/taxLegalTypes";

import Icon from "../../../assets/icons/icon";
import DateSelect from "../../../components/form/dateSelect";
import SelectUser from "../../../components/form/selectUser";
import StatusSelect from "../../../components/form/statusSelect";
import DividerLine from "../../../components/lineSplit";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import _t from "../../../lang/translate";
import { getSavedEnums } from "../../../services/authService";
import { updateTaxLegalCase } from "../../../services/taxLegalService";
import consts from "../../../utilities/consts";
import format from "../../../utilities/formatNumbers";
import { isValidNum } from "../../../utilities/typeGuard";
import { PenaltyType } from "../shared";
import TaxLegalCaseAttachments from "../taxLegalAttachments";
import TaxLegalHeadKeyFields from "./taxLegalHeadKeyFields";
import TaxLegalVehicleCard from "./taxLegalVehicleCard";

const DRAFT_ENUM_ID = 40;
const PENALTY_CASE_ENUM_ID = 4;

export default function TaxLegalCaseHead() {
  const taxLegalCase = useTaxLegalCase();
  const { user } = UserStore.useContainer();

  const { getStagesForms, stages, setStages } = useTaxLegalStages();
  const { setTaxLegalCase } = useTaxLegalActions();
  const { vehicle, dealerName } = taxLegalCase;
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<TaxLegalCaseRevision>();
  const [attachmentsVisible, setAttachmentsVisible] = useState(false)
  const initialValues: Partial<TaxLegalCaseRevision> = {
    statusId: taxLegalCase.status?.id,
    nextDeadline: taxLegalCase.nextDeadline || undefined,
    note: taxLegalCase.importantNote || undefined,
    penaltyCaseId: taxLegalCase.penaltyCaseId || undefined,
    penaltyTypeId: taxLegalCase.penaltyType?.id,
    // @ts-ignore
    allDMRDocumentsRequestedAt: !!taxLegalCase.allDMRDocumentsRequestedAt,
    caseHandlerId: taxLegalCase.caseHandler?.id,
    repairRequested: !!taxLegalCase.repairRequestedAt,
    obsoleted: !!taxLegalCase.obsoletedAt,
    governmentBatchId: taxLegalCase.governmentBatchId,
  };


  async function handleSaveCase(revisionValues?: TaxLegalCaseRevision) {
    try {
      let newTaxLegalCase = structuredClone(taxLegalCase) as UpdateTaxLegalCase;
      if (!!revisionValues && !revisionValues.caseHandlerId) {
        revisionValues.caseHandlerId = null
      }
      if (revisionValues) {
        newTaxLegalCase.revision = revisionValues;
      }
      setSaving(true);
      const stagesForms = getStagesForms();

      if (stagesForms) {
        const newStages: TaxLegalStage[] = [];
        stagesForms.forEach((formStage, fingerprint) => {
          const stage = structuredClone(stages.find((stage) => stage.fingerprint === fingerprint));
          if (stage) {
            const data = formStage.data;
            for (const [key, formInstance] of Object.entries(data) as [StageFormNames, any][]) {
              stage.data[key] = formInstance.getFieldsValue(true);
            }
            newStages.push(stage)
          }
        });
        newTaxLegalCase.stages = newStages;
        newTaxLegalCase.activeStageValues = constructKeyValues(
          newStages,
          taxLegalCase.originalEvaluation,
        );
      }
      // const { data } = await updateTaxLegalCase(taxLegalCase.id, newTaxLegalCase);
      const { data } = await updateTaxLegalCase(taxLegalCase.id, newTaxLegalCase);
      setTaxLegalCase(data);
      setStages(data.stages);
    } catch (error) {
      renderErrorMessage(error);
    } finally {
      setSaving(false);
      if (saveModalOpen) {
        setSaveModalOpen(false);
      }
    }
  }

  return (
    <div>
      <TaxLegalVehicleCard vehicle={vehicle} />
      <DividerLine fluid style={{ marginBottom: 12, marginTop: 12 }} />
      <Row align="middle" gutter={[8, 16]}>
        <Col flex="auto"> <TaxLegalHeadKeyFields /> </Col>
        <Col>
          <Space wrap>
            <Button onClick={() => setAttachmentsVisible(true)}
              icon={taxLegalCase.files?.length ? (
                <Icon name="FolderStar" size='large' />
              ) : (
                <Icon name="folder" />
              )}
            />
            <Drawer width={500}
              onClose={() => setAttachmentsVisible(false)}
              visible={attachmentsVisible}
            >
              <TaxLegalCaseAttachments isModal />
            </Drawer>

            <Button loading={saving} onClick={() => handleSaveCase()}>
              {_t("saveWithoutStatus")}
            </Button>
            <Button loading={saving} type="primary" onClick={() => setSaveModalOpen(true)}>
              {_t("save")}
            </Button>

            <Modal
              title={_t("save", "case")}
              visible={saveModalOpen}
              onCancel={() => setSaveModalOpen(false)}
              className="dmf-modal-footer-space-between"
              okText={_t("save")}
              onOk={form.submit}
              confirmLoading={saving}
              cancelButtonProps={{ disabled: saving }}
              width={600}
            >
              <Form
                form={form}
                {...consts.formItemProps}
                // TODO --> Get next deadline from forms
                requiredMark={false}
                onFinish={handleSaveCase}
                initialValues={initialValues}
              >
                <Form.Item rules={[{ required: true }]} label={_t("status")} name="statusId">
                  <StatusSelect disabled={saving} contexts="taxLegal" />
                </Form.Item>
                <Form.Item label={_t("next", "deadline")} name="nextDeadline">
                  <DateSelect disabled={saving} />
                </Form.Item>
                {user?.dealer.id && (
                  <Form.Item
                    label={_t("caseHandler")}
                    shouldUpdate={(prev, next) => prev.statusId !== next.statusId}
                  >
                    {({ getFieldValue }) => {
                      const status = getFieldValue(["statusId"]) as number;

                      return (
                        <SelectUser
                          dealerId={user.dealer.id}
                          typeId={1}
                          name="caseHandlerId"
                          allowClear={true}
                          noStyle
                        />
                      );
                    }}
                  </Form.Item>
                )}
                <Form.Item label={_t("responsibilityEvaluation")} name="penaltyTypeId">
                  <PenaltyType />
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, next) => prev.penaltyTypeId !== next.penaltyTypeId}
                >
                  {({ getFieldValue }) => {
                    const penaltyId = getFieldValue(["penaltyTypeId"]);
                    const penaltyCaseEnum = getSavedEnums()?.taxLegalCase?.penaltyType?.find(
                      (item) => item.id === PENALTY_CASE_ENUM_ID,
                    );
                    if (penaltyCaseEnum && penaltyId !== penaltyCaseEnum.id) return null;
                    return (
                      <Form.Item
                        rules={[{ required: true }]}
                        label={_t("penaltyCaseId")}
                        name="penaltyCaseId"
                      >
                        <Input disabled={saving} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                <Form.Item label={_t("notice")} name="note">
                  <Input disabled={saving} />
                </Form.Item>
                <Form.Item label={_t("group", "id")} name="governmentBatchId">
                  <Input disabled={saving} />
                </Form.Item>
              </Form>
            </Modal>
          </Space>
        </Col>
      </Row>
    </div>
  );
}

function constructKeyValues(
  stages: TaxLegalStage[],
  originalEvaluation: OriginalEvaluation | null,
): ActiveStageValues {
  const activeStageValues: Partial<ActiveStageValues> = {
    activeStage: stages[stages.length - 1].type,
  };

  const priceKeys: { formKey: string; stageValueKey: keyof ActiveStageValues }[] = [
    { formKey: "newPrice", stageValueKey: "mstNewPrice" },
    { formKey: "equipmentPrice", stageValueKey: "mstEquipmentPrice" },
    { formKey: "tradePrice", stageValueKey: "mstTradePrice" },
    { formKey: "registrationFee", stageValueKey: "mstRegistrationFee" },
    { formKey: "regulationAmount", stageValueKey: "regulationAmount" },
  ];

  const statuteOfLimitationDate = calculateStatuteOfLimitationDate(originalEvaluation?.reviewDate);
  if (statuteOfLimitationDate) {
    activeStageValues.statuteOfLimitationsDate = statuteOfLimitationDate.toISOString();
  }

  const formOrderByStage: Record<TaxLegalStage['type'], StageFormNames[]> = {
    price_check: ['materials', 'hearingOfParties', 'conclusion'],
    complaint: ['submission', 'casePresentation', 'complaintConclusion'],
    resumption: ['request', 'hearingOfParties', 'conclusion']
  }

  const governmentCaseNumbers: string[] = [];
  for (const stage of stages) {
    const formOrder = formOrderByStage[stage.type]
    for (const formName of formOrder) {
      for (const key of priceKeys) {
        const formObj = stage.data[formName as StageFormNames] as any;
        if (!formObj) {
          continue;
        }
        if (key.formKey in formObj && isValidNum(formObj[key.formKey])) {
          (activeStageValues[key.stageValueKey] as number) = Number(formObj[key.formKey]);
        }
      }
    }

    if (stage.type === "price_check") {
      if (stage.data.materials) {
        if (stage.data.materials.governmentCaseNumber) {
          governmentCaseNumbers.push(stage.data.materials.governmentCaseNumber)
        }
        if (Array.isArray(stage.data.materials.analysisTags)) {
          activeStageValues.analysisTags = stage.data.materials.analysisTags as string[];
        }
      }
      if (stage.data.hearingOfParties) {
        if (Array.isArray(stage.data.hearingOfParties.lackOfComplianceTags)) {
          activeStageValues.complianceTags = stage.data.hearingOfParties
            .lackOfComplianceTags as string[];
        }
        if (stage.data.hearingOfParties.governmentNewPriceAssessment) {
          activeStageValues.governmentNewPriceAssessment =
            stage.data.hearingOfParties.governmentNewPriceAssessment;
        }
        if (stage.data.hearingOfParties.governmentTradePriceAssessment) {
          activeStageValues.governmentTradePriceAssessment =
            stage.data.hearingOfParties.governmentTradePriceAssessment;
        }
      }
      if (stage.data.conclusion) {
        if (stage.data.conclusion.lackOfComplianceTags) {
          activeStageValues.complianceTags = stage.data.conclusion.lackOfComplianceTags as string[];
        }
        if (stage.data.conclusion.conclusionDate) {
          activeStageValues.conclusionDate = stage.data.conclusion.conclusionDate;
        }
        if (stage.data.conclusion.governmentNewPriceAssessment) {
          activeStageValues.governmentNewPriceAssessment =
            stage.data.conclusion.governmentNewPriceAssessment;
        }
        if (stage.data.conclusion.governmentTradePriceAssessment) {
          activeStageValues.governmentTradePriceAssessment =
            stage.data.conclusion.governmentTradePriceAssessment;
        }
      }
    } else if (stage.type === "complaint") {
      if (stage.data.casePresentation && stage.data.casePresentation.caseHandlerIdSANST) {
        activeStageValues.governmentCaseHandlerId = stage.data?.casePresentation?.caseHandlerIdSANST;
      }
      if (stage.data.complaintConclusion) {
        if (stage.data.complaintConclusion.conclusionDate) {
          activeStageValues.conclusionDate = stage.data.complaintConclusion.conclusionDate;
        }
      }
      if (stage.data.casePresentation) {
        if (stage.data.casePresentation.caseIdSANST) {
          governmentCaseNumbers.push(stage.data.casePresentation.caseIdSANST)
        }
      }

    } else if (stage.type === "resumption") {
      if (stage.data.request) {
        if (stage.data.request.receptionGovernmentCaseHandlerId) {
          activeStageValues.governmentCaseHandlerId = stage.data.request.receptionGovernmentCaseHandlerId;
        }
        if (stage.data.request.submissionGovernmentCaseNumber) {
          governmentCaseNumbers.push(stage.data.request.submissionGovernmentCaseNumber)
        }
        if (stage.data.request.receptionGovernmentCaseNumber) {
          governmentCaseNumbers.push(stage.data.request.receptionGovernmentCaseNumber)
        }
      }
      if (stage.data.hearingOfParties) {
        if (stage.data.hearingOfParties.governmentCaseNumber) {
          governmentCaseNumbers.push(stage.data.hearingOfParties.governmentCaseNumber)
        }
        if (Array.isArray(stage.data.hearingOfParties.lackOfComplianceTags)) {
          activeStageValues.complianceTags = stage.data.hearingOfParties
            .lackOfComplianceTags as string[];
        }
        if (stage.data.hearingOfParties.governmentNewPriceAssessment) {
          activeStageValues.governmentNewPriceAssessment =
            stage.data.hearingOfParties.governmentNewPriceAssessment;
        }
        if (stage.data.hearingOfParties.governmentTradePriceAssessment) {
          activeStageValues.governmentTradePriceAssessment =
            stage.data.hearingOfParties.governmentTradePriceAssessment;
        }
      }
      if (stage.data.conclusion) {
        if (stage.data.conclusion.governmentCaseNumber) {
          governmentCaseNumbers.push(stage.data.conclusion.governmentCaseNumber)
        }
        if (stage.data.conclusion.conclusionDate) {
          activeStageValues.conclusionDate = stage.data.conclusion.conclusionDate;
        }
        if (stage.data.conclusion.governmentCaseHandlerId) {
          activeStageValues.governmentCaseHandlerId = stage.data.conclusion.governmentCaseHandlerId;
        }
        if (stage.data.conclusion.governmentNewPriceAssessment) {
          activeStageValues.governmentNewPriceAssessment =
            stage.data.conclusion.governmentNewPriceAssessment;
        }
        if (stage.data.conclusion.governmentTradePriceAssessment) {
          activeStageValues.governmentTradePriceAssessment =
            stage.data.conclusion.governmentTradePriceAssessment;
        }
      }
    }
  }

  if (governmentCaseNumbers.length) {
    activeStageValues.governmentCaseNumber = governmentCaseNumbers.join(",");
  }

  if (isValidNum(activeStageValues.regulationAmount)) {
    let startingRegulationAmount = 0;
    if (isValidNum(stages[0].data.hearingOfParties?.regulationAmount)) {
      startingRegulationAmount = stages[0].data.hearingOfParties.regulationAmount;
    } else if (isValidNum(stages[0].data.conclusion?.regulationAmount)) {
      startingRegulationAmount = stages[0].data.conclusion.regulationAmount;
    }
    activeStageValues.regulationReduction =
      activeStageValues.regulationAmount - startingRegulationAmount;
  }
  return activeStageValues as ActiveStageValues;
}

function calculateStatuteOfLimitationDate(reviewDate: string | null | undefined) {
  if (!reviewDate) return null;
  // 15. of next month, plus 3 years, minus 1 month for buffer-room.
  return moment(reviewDate).set("date", 15).add(3, "years").add(1, "month");
}
